/**
 * Spec 038: «Инструменты сотрудников» — the external services the company's agents generate
 * with (Higgsfield first). The tasker makes every call with the tool's key, so the key never
 * reaches a PC. Partners register a tool here; the key is bound by the person who stored it in
 * «Секреты»; a card grants the tool with a daily limit. Below — what the staff generated.
 * A tab of «Отделы» (`?tab=tools`), not a page of its own: the header and the tabs stay.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, fetchBlob } from "@/api/client";
import { CompanyTools, type CompanyToolOut, type ToolCallOut, type ToolModel } from "@/api/units";
import { SecretPicker } from "@/components/AgentForm";
import { Button } from "@/components/ui/Button";
import { TextArea, TextField } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { ago, Caption, EmptyNote, Pill } from "@/kit";
import { askLine, CALL_STATE, callTone, fileLabel, keyProblem, toolLine } from "@/lib/tools";

export function ToolsBlock() {
  const tools = useQuery({ queryKey: ["company", "tools"], queryFn: () => CompanyTools.list(true) });
  const calls = useQuery({ queryKey: ["company", "tools", "calls"], queryFn: () => CompanyTools.calls(), refetchInterval: 30_000 });
  const [editing, setEditing] = useState<CompanyToolOut | "new" | null>(null);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-meta text-p-text-muted">
        Внешние сервисы, которыми агенты отделов генерируют видео, картинки и звук. Вызов делает задачник со своим ключом — ключ не попадает на ПК. Агенту инструмент выдают в его «Настройках» с лимитом генераций в сутки.
      </p>
      {tools.isLoading && <SkeletonList count={2} />}
      {tools.data && !tools.data.length && <EmptyNote>Инструментов пока нет.</EmptyNote>}
      {(tools.data ?? []).length > 0 && (
        <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
          {(tools.data ?? []).map((t) => (
            <li key={t.id} className="border-b border-p-border last:border-0">
              <button type="button" className="flex w-full flex-col gap-1 px-4 py-3 text-left press-surface" onClick={() => setEditing(t)}>
                <span className="text-[15px] font-semibold leading-5 text-p-text">{t.title} <span className="font-normal text-p-text-muted">· {t.key}</span></span>
                <span className={t.has_key && !t.archived_at ? "text-meta text-p-text-muted" : "text-meta text-p-needs"}>{toolLine(t)}</span>
                {t.models.length > 0 && <span className="text-meta text-p-text-muted">Моделей: {t.models.length}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div>
        <Button variant="secondary" onClick={() => setEditing("new")}>Завести инструмент</Button>
      </div>

      <section className="flex flex-col gap-2" aria-label="Генерации">
        <Caption>Генерации</Caption>
        {calls.isLoading && <SkeletonList count={2} />}
        {calls.data && !calls.data.length && <p className="text-meta text-p-text-muted">Агенты пока ничего не генерировали.</p>}
        <ul className="flex flex-col gap-2">
          {(calls.data ?? []).map((c) => <CallRow key={c.id} call={c} />)}
        </ul>
      </section>

      {editing && <ToolSheet tool={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function CallRow({ call }: { call: ToolCallOut }) {
  const toast = useToast();
  const [shown, setShown] = useState<Record<number, string>>({});
  const open = async (n: number, url: string) => {
    try {
      const blob = await fetchBlob(url);
      setShown((now) => ({ ...now, [n]: URL.createObjectURL(blob) }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Файл не открылся");
    }
  };
  return (
    <li className="flex flex-col gap-1.5 rounded-card border border-p-border bg-p-surface px-4 py-3">
      <span className="text-meta text-p-text-muted">
        {call.agent.name} · {call.tool_title}
        {call.order_number ? ` · поручение №${call.order_number}` : ""} · {ago(call.created_at)}
      </span>
      <span className="break-words text-body text-p-text">{askLine(call)}</span>
      <span className="flex flex-wrap items-center gap-2">
        <Pill tone={callTone(call.state)}>{CALL_STATE[call.state]}</Pill>
        <span className="text-meta text-p-text-muted">{call.model}</span>
      </span>
      {call.error && <p className="break-words text-meta text-p-blocked">{call.error}</p>}
      {call.files.map((f) =>
        f.url ? (
          <div key={f.n} className="flex flex-col gap-1">
            {shown[f.n] ? (
              f.kind === "video" ? (
                <video src={shown[f.n]} controls className="max-h-80 w-full rounded-card bg-black" />
              ) : f.kind === "image" ? (
                <img src={shown[f.n]} alt={fileLabel(f)} className="max-h-80 w-auto self-start rounded-card" />
              ) : f.kind === "audio" ? (
                <audio src={shown[f.n]} controls className="w-full" />
              ) : (
                <a href={shown[f.n]} download className="text-meta font-semibold text-p-link">Скачать: {fileLabel(f)}</a>
              )
            ) : (
              <button type="button" className="min-h-11 self-start text-meta font-semibold text-p-link" onClick={() => void open(f.n, f.url!)}>
                Открыть: {fileLabel(f)}
              </button>
            )}
          </div>
        ) : (
          <span key={f.n} className="text-meta text-p-text-muted">{fileLabel(f)} — не сохранён</span>
        ),
      )}
    </li>
  );
}

const EMPTY_MODEL: ToolModel = { path: "", title: "", notes_md: "" };

function ToolSheet({ tool, onClose }: { tool: CompanyToolOut | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [key, setKey] = useState(tool?.key ?? "higgsfield");
  const [title, setTitle] = useState(tool?.title ?? "Higgsfield");
  const [notes, setNotes] = useState(tool?.notes_md ?? "");
  const [models, setModels] = useState<ToolModel[]>(tool?.models ?? []);
  // Starts on the bound key: the picker shows what is bound, not an empty choice.
  const [secret, setSecret] = useState<string | null>(tool?.secret_id ?? null);
  const done = (text: string) => {
    toast.success(text);
    void qc.invalidateQueries({ queryKey: ["company", "tools"] });
  };
  const fail = (e: Error) => {
    toast.error(e.message);
    if (e instanceof ApiError && e.status === 409) void qc.invalidateQueries({ queryKey: ["company", "tools"] });
  };
  const clean = models.filter((m) => m.path.trim() && m.title.trim()).map((m) => ({ ...m, notes_md: m.notes_md?.trim() || null }));
  const save = useMutation({
    mutationFn: () =>
      tool
        ? CompanyTools.update(tool.id, { version: tool.version, title: title.trim(), notes_md: notes.trim() || null, models: clean })
        : CompanyTools.create({ key: key.trim().toLowerCase(), title: title.trim(), notes_md: notes.trim() || null, models: clean }),
    onSuccess: () => {
      done(tool ? "Инструмент сохранён" : "Инструмент заведён: привяжи к нему ключ");
      onClose();
    },
    onError: fail,
  });
  const bind = useMutation({
    mutationFn: (secretId: string | null) => CompanyTools.bindKey(tool!.id, tool!.version, secretId),
    onSuccess: (saved) => {
      done(saved.has_key ? "Ключ привязан" : "Ключ отвязан");
      onClose();
    },
    onError: fail,
  });
  const archive = useMutation({
    mutationFn: () => CompanyTools.update(tool!.id, { version: tool!.version, archived: !tool!.archived_at }),
    onSuccess: (saved) => {
      done(saved.archived_at ? "Инструмент в архиве" : "Инструмент вернулся");
      onClose();
    },
    onError: fail,
  });
  const check = useMutation({
    mutationFn: () => CompanyTools.check(tool!.id),
    onSuccess: (r) => (r.ok ? toast.success(r.message) : toast.error(r.message)),
    onError: fail,
  });
  const problem = tool ? "" : keyProblem(key);

  return (
    <Sheet open onClose={onClose} title={tool ? tool.title : "Новый инструмент"}
      footer={<Button block variant="strong" loading={save.isPending} disabled={!title.trim() || !!problem} onClick={() => save.mutate()}>Сохранить</Button>}>
      <div className="flex flex-col gap-4">
        {!tool && <TextField label="Ключ" hint={problem || "По нему карточка выдаёт инструмент и называется лимит"} value={key} onChange={(e) => setKey(e.target.value)} />}
        <TextField label="Название" value={title} onChange={(e) => setTitle(e.target.value)} />
        <TextArea label="Заметки" hint="Что умеет, сколько стоит, на что смотреть" value={notes} rows={3} maxLength={4000} onChange={(e) => setNotes(e.target.value)} />
        <section className="flex flex-col gap-2">
          <Caption>Модели</Caption>
          <p className="text-meta text-p-text-muted">Путь модели из документации сервиса и её поля. Пусто — агенту можно любую модель.</p>
          {models.map((m, i) => (
            <div key={i} className="flex flex-col gap-2 rounded-card border border-p-border px-3 py-2">
              <TextField label="Путь" placeholder="higgsfield-ai/soul/v2/standard" value={m.path}
                onChange={(e) => setModels(models.map((x, j) => (j === i ? { ...x, path: e.target.value } : x)))} />
              <TextField label="Что это" placeholder="Картинка по тексту" value={m.title}
                onChange={(e) => setModels(models.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
              <TextArea label="Поля и ограничения" rows={2} maxLength={2000} value={m.notes_md ?? ""}
                onChange={(e) => setModels(models.map((x, j) => (j === i ? { ...x, notes_md: e.target.value } : x)))} />
              <Button size="compact" variant="ghost" onClick={() => setModels(models.filter((_, j) => j !== i))}>Убрать модель</Button>
            </div>
          ))}
          <div><Button size="compact" variant="secondary" onClick={() => setModels([...models, { ...EMPTY_MODEL }])}>Модель</Button></div>
        </section>
        {tool && (
          <section className="flex flex-col gap-2">
            <Caption>Ключ</Caption>
            <p className="text-meta text-p-text-muted">
              {tool.has_key
                ? `Привязан: ${tool.secret_name ?? "секрет"}${tool.key_bound_by ? ` · ${tool.key_bound_by.name}` : ""}.`
                : "Не привязан — агенты не могут им пользоваться."}{" "}
              Привязывает тот, кто сохранил ключ в «Бизнес» → карточка ресурса → «Секреты».
            </p>
            <SecretPicker label="Мой секрет с ключом" value={secret} onChange={setSecret} mineOnly />
            <div className="flex flex-wrap gap-2">
              <Button size="compact" variant="strong" disabled={!secret || secret === tool.secret_id} loading={bind.isPending} onClick={() => bind.mutate(secret)}>Привязать</Button>
              {tool.has_key && <Button size="compact" variant="secondary" loading={check.isPending} onClick={() => check.mutate()}>Проверить ключ</Button>}
              {tool.has_key && <Button size="compact" variant="secondary" loading={bind.isPending} onClick={() => bind.mutate(null)}>Отвязать</Button>}
            </div>
          </section>
        )}
        {tool && (
          <button type="button" className="min-h-11 self-start text-meta font-semibold text-p-link" disabled={archive.isPending} onClick={() => archive.mutate()}>
            {tool.archived_at ? "Вернуть из архива" : "Убрать в архив"}
          </button>
        )}
      </div>
    </Sheet>
  );
}
